<template>
  <div id="judges-list" class="jdx">
    <overlay-loading-progress
      :LoadingFlag="OverlayLoadingFlag"
      :LoadingText="OverlayLoadingText"
    ></overlay-loading-progress>

    <!-- Ambient background (decorative only) -->
    <div class="jdx-ambient" aria-hidden="true">
      <span class="jdx-glow jdx-glow--aqua"></span>
      <span class="jdx-glow jdx-glow--navy"></span>
      <span class="jdx-glow jdx-glow--seafoam"></span>
    </div>

    <!-- Hero -->
    <section class="jdx-hero jdx-reveal" style="--d: 0ms">
      <div class="jdx-hero__decor" aria-hidden="true">
        <span class="jdx-orb jdx-orb--1"></span>
        <span class="jdx-orb jdx-orb--2"></span>
        <span class="jdx-ring jdx-ring--1"></span>
        <span class="jdx-ring jdx-ring--2"></span>
        <span class="jdx-spark jdx-spark--1"></span>
        <span class="jdx-spark jdx-spark--2"></span>
        <span class="jdx-spark jdx-spark--3"></span>
        <span class="jdx-spark jdx-spark--4"></span>
        <!-- Fanned judge scorecards -->
        <svg class="jdx-hero__cards" viewBox="0 0 220 140" fill="none">
          <g class="jdx-card jdx-card--1">
            <rect x="20" y="30" width="78" height="100" rx="10" />
            <line x1="34" y1="54" x2="84" y2="54" />
            <line x1="34" y1="70" x2="74" y2="70" />
            <line x1="34" y1="86" x2="80" y2="86" />
          </g>
          <g class="jdx-card jdx-card--2">
            <rect x="72" y="14" width="78" height="100" rx="10" />
            <line x1="86" y1="38" x2="136" y2="38" />
            <line x1="86" y1="54" x2="126" y2="54" />
            <line x1="86" y1="70" x2="132" y2="70" />
            <circle cx="111" cy="94" r="9" />
          </g>
          <g class="jdx-card jdx-card--3">
            <rect x="124" y="30" width="78" height="100" rx="10" />
            <line x1="138" y1="54" x2="188" y2="54" />
            <line x1="138" y1="70" x2="178" y2="70" />
            <line x1="138" y1="86" x2="184" y2="86" />
          </g>
        </svg>
      </div>

      <div class="jdx-hero__top">
        <div class="jdx-hero__grid">
          <span class="jdx-hero__icon">
            <v-icon color="white" size="34">mdi-gavel</v-icon>
          </span>
          <div class="jdx-hero__copy">
            <div class="jdx-hero__eyebrow">
              JCI Awards <span class="jdx-dot" aria-hidden="true"></span> Judges
              <em>Panel</em>
            </div>
            <h1 class="jdx-hero__title">{{ PageTitle || "Judges List" }}</h1>
            <span class="jdx-hero__accent" aria-hidden="true"></span>
            <p class="jdx-hero__desc">
              {{ PageDescription || "Manage award judges, access and status" }}
            </p>
          </div>
        </div>

        <div class="jdx-hero__actions">
          <v-btn
            @click.prevent="refreshPageData"
            :loading="LoadingFlag"
            depressed
            class="jdx-glass-btn"
            aria-label="Refresh"
          >
            <v-icon left class="jdx-glass-btn__icon jdx-spin-hover"
              >mdi-refresh</v-icon
            >
            <span class="jdx-glass-btn__text">Refresh</span>
          </v-btn>
          <v-btn
            to="/jci-award/judges/upload"
            depressed
            class="jdx-glass-btn"
            aria-label="Upload"
          >
            <v-icon left class="jdx-glass-btn__icon"
              >mdi-cloud-upload-outline</v-icon
            >
            <span class="jdx-glass-btn__text">Upload</span>
          </v-btn>
          <v-btn to="/jci-award/judges/create" depressed class="jdx-gold-btn">
            <v-icon left class="jdx-gold-btn__icon"
              >mdi-account-plus-outline</v-icon
            >
            Create Judge
          </v-btn>
        </div>
      </div>

      <!-- Stats -->
      <div class="jdx-stats">
        <div class="jdx-stat jdx-stat--total jdx-reveal" style="--d: 120ms">
          <span class="jdx-stat__icon">
            <v-icon size="20" color="white">mdi-account-group-outline</v-icon>
          </span>
          <div class="jdx-stat__body">
            <div class="jdx-stat__value">{{ tableData1.length }}</div>
            <div class="jdx-stat__label">Total Judges</div>
          </div>
        </div>
        <div class="jdx-stat jdx-stat--active jdx-reveal" style="--d: 180ms">
          <span class="jdx-stat__icon">
            <v-icon size="20" color="white">mdi-check-decagram</v-icon>
          </span>
          <div class="jdx-stat__body">
            <div class="jdx-stat__value">{{ ActiveCount }}</div>
            <div class="jdx-stat__label">Active</div>
            <div class="jdx-meter" aria-hidden="true">
              <span
                class="jdx-meter__fill"
                :style="{
                  width: tableData1.length
                    ? (ActiveCount / tableData1.length) * 100 + '%'
                    : '0%',
                }"
              ></span>
            </div>
          </div>
        </div>
        <div class="jdx-stat jdx-stat--inactive jdx-reveal" style="--d: 240ms">
          <span class="jdx-stat__icon">
            <v-icon size="20" color="white">mdi-account-off-outline</v-icon>
          </span>
          <div class="jdx-stat__body">
            <div class="jdx-stat__value">{{ InactiveCount }}</div>
            <div class="jdx-stat__label">Inactive</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Judge finder -->
    <section class="jdx-studio jdx-reveal" style="--d: 120ms">
      <header class="jdx-studio__head">
        <div class="jdx-head">
          <span class="jdx-head__icon jdx-head__icon--navy">
            <v-icon color="white" size="22">mdi-account-search-outline</v-icon>
          </span>
          <div>
            <h2 class="jdx-head__title">Judge Finder</h2>
            <p class="jdx-head__desc">
              Find judges by zone and type, or by membership id
            </p>
          </div>
        </div>
        <v-btn-toggle
          v-if="!LoadingFlag"
          v-model="SearchType"
          mandatory
          class="jdx-segmented"
        >
          <v-btn :value="1" depressed>
            <v-icon left small>mdi-map-marker-outline</v-icon>
            By Zone
          </v-btn>
          <v-btn :value="2" depressed>
            <v-icon left small>mdi-card</v-icon>
            By Membership Id
          </v-btn>
        </v-btn-toggle>
      </header>

      <div v-if="LoadingFlag" class="jdx-loading">
        <div class="jdx-loading__orb">
          <span class="jdx-loading__halo" aria-hidden="true"></span>
          <v-progress-circular
            indeterminate
            size="56"
            width="4"
            color="#0097d7"
          ></v-progress-circular>
          <v-icon class="jdx-loading__icon" color="#3a67b1" size="20"
            >mdi-gavel</v-icon
          >
        </div>
        <p class="jdx-loading__text">Loading judges data...</p>
      </div>

      <v-form ref="form1" v-model="valid1" lazy-validation v-if="!LoadingFlag">
        <div class="jdx-fields">
          <div class="jdx-field" v-if="SearchType == 2">
            <label class="jdx-field__label">
              <span class="jdx-field__icon jdx-field__icon--navy">
                <v-icon size="16" color="white"
                  >mdi-account-card-details-outline</v-icon
                >
              </span>
              <span><span class="jdx-req">*</span> Membership Id</span>
            </label>
            <v-text-field
              v-model.trim="MembershipId"
              :rules="MembershipIdRules"
              placeholder="Enter membership id"
              outlined
              dense
              clearable
              required
              @keyup.enter="submitForm"
            ></v-text-field>
          </div>
          <div class="jdx-field" v-if="SearchType == 1">
            <label class="jdx-field__label">
              <span class="jdx-field__icon jdx-field__icon--navy">
                <v-icon size="16" color="white">mdi-map-marker-outline</v-icon>
              </span>
              <span><span class="jdx-req">*</span> Zone</span>
            </label>
            <v-autocomplete
              :items="ZoneCodeOptions"
              :rules="ZoneCodeRules"
              v-model="ZoneCode"
              placeholder="Select zone"
              outlined
              dense
              clearable
              required
            ></v-autocomplete>
          </div>
          <div class="jdx-field" v-if="SearchType == 1">
            <label class="jdx-field__label">
              <span class="jdx-field__icon jdx-field__icon--aqua">
                <v-icon size="16" color="white">mdi-account-tie</v-icon>
              </span>
              <span>Judge Type</span>
            </label>
            <v-autocomplete
              :items="JudgeTypeOptions"
              :rules="JudgeTypeRules"
              v-model="JudgeType"
              placeholder="All judge types"
              outlined
              dense
              clearable
            ></v-autocomplete>
          </div>
        </div>

        <div class="jdx-actions">
          <span class="jdx-required-note">
            <span class="jdx-req">*</span> indicates required field
          </span>
          <v-btn
            :disabled="!valid1"
            @click.prevent="submitForm"
            :loading="SubmitFlag"
            depressed
            class="jdx-btn jdx-btn--search"
          >
            <v-icon left class="jdx-btn__icon">mdi-magnify</v-icon>
            Search Judges
          </v-btn>
        </div>
      </v-form>
    </section>

    <!-- Results -->
    <transition name="jdx-fade" mode="out-in">
      <section
        v-if="tableData1.length > 0"
        key="results"
        class="jdx-group jdx-reveal"
        style="--d: 0ms"
      >
        <header class="jdx-group__head">
          <span class="jdx-group__icon">
            <v-icon color="white" size="20">mdi-account-tie</v-icon>
          </span>
          <div class="jdx-group__copy">
            <h2 class="jdx-group__title">
              Judges Panel
              <span class="jdx-count">{{ tableData1.length }}</span>
            </h2>
            <p class="jdx-group__desc">
              Toggle the switch to activate or deactivate a judge
            </p>
          </div>
          <div class="jdx-legend">
            <span class="jdx-legend__item">
              <span class="jdx-legend__dot jdx-legend__dot--active"></span>
              {{ ActiveCount }} Active
            </span>
            <span class="jdx-legend__item">
              <span class="jdx-legend__dot jdx-legend__dot--inactive"></span>
              {{ InactiveCount }} Inactive
            </span>
          </div>
        </header>

        <div class="jdx-table-card">
          <div class="jdx-toolbar">
            <v-text-field
              v-model="search"
              placeholder="Search name, zone, mobile, email..."
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              clearable
              hide-details
              class="jdx-toolbar__search"
            ></v-text-field>
            <v-btn
              v-if="DownloadFlag"
              @click="downloadExcel"
              depressed
              class="jdx-btn jdx-btn--export"
            >
              <v-icon left class="jdx-btn__icon">mdi-microsoft</v-icon>
              Export to Excel
            </v-btn>
          </div>

          <v-data-table
            v-model="selected1"
            :search="search"
            :item-key="tableOptions1.ItemKey"
            :headers="OrderedColumns"
            :items="tableData1"
            :items-per-page="tableOptions1.ItemsPerPage"
            :single-select="tableOptions1.SingleSelectFlag"
            :show-select="tableOptions1.ShowSelectFlag"
            :footer-props="tableOptions1.FooterProps"
            class="jdx-table"
          >
            <!-- Actions Column -->
            <template v-slot:item.Actions="{ item }">
              <div class="jdx-row-actions">
                <v-tooltip bottom v-if="item.PreviewFlag">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      @click="previewAlert(item)"
                      v-bind="attrs"
                      v-on="on"
                      class="jdx-row-action jdx-row-action--view"
                    >
                      <v-icon small>mdi-eye-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>View Details</span>
                </v-tooltip>

                <v-tooltip bottom v-if="item.DeleteButtonFlag">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      @click="deleteAlert(item)"
                      v-bind="attrs"
                      v-on="on"
                      class="jdx-row-action jdx-row-action--delete"
                    >
                      <v-icon small>mdi-trash-can-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Delete</span>
                </v-tooltip>
              </div>
            </template>

            <!-- Status Column -->
            <template v-slot:item.ActiveStatusTxt="{ item }">
              <div class="jdx-status">
                <v-switch
                  :key="
                    item.JudgeId + '-' + item.ActiveStatus + '-' + SwitchKey
                  "
                  :input-value="item.ActiveStatus == 1"
                  :disabled="
                    !item.EditButtonFlag || StatusUpdatingId == item.JudgeId
                  "
                  :loading="
                    StatusUpdatingId == item.JudgeId ? 'primary' : false
                  "
                  @change="statusToggleAlert(item, $event)"
                  color="#56BDA3"
                  inset
                  dense
                  hide-details
                  class="jdx-switch mt-0 pt-0"
                ></v-switch>
                <span
                  :class="[
                    'jdx-pill',
                    item.ActiveStatus == 1 ? 'is-active' : 'is-inactive',
                  ]"
                  >{{ item.ActiveStatusTxt }}</span
                >
              </div>
            </template>

            <!-- Judge Name Column -->
            <template v-slot:item.MemberFullNameTxt="{ item }">
              <span class="jdx-name">{{ item.MemberFullNameTxt }}</span>
            </template>

            <!-- Judge Type Column -->
            <template v-slot:item.RoleName="{ item }">
              <span class="jdx-tag">{{ item.RoleName }}</span>
            </template>

            <!-- Contact Column -->
            <template v-slot:item.ContactTxt="{ item }">
              <div v-html="item.ContactTxt" class="jdx-contact"></div>
            </template>

            <!-- Image Column -->
            <template v-slot:item.MemberImage="{ item }">
              <v-avatar size="44" class="jdx-avatar">
                <img :src="item.MemberImage" alt="Judge" />
              </v-avatar>
            </template>

            <!-- Credentials -->
            <template v-slot:item.UserName="{ item }">
              <code class="jdx-mono">{{ item.UserName }}</code>
            </template>
            <template v-slot:item.Password="{ item }">
              <div class="jdx-secret">
                <code class="jdx-mono">{{ item.Password }}</code>
                <v-tooltip bottom v-if="item.Password">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      @click="copyPassword(item)"
                      v-bind="attrs"
                      v-on="on"
                      class="jdx-row-action jdx-row-action--copy"
                      aria-label="Copy password"
                    >
                      <v-icon small>{{
                        CopiedJudgeId == item.JudgeId
                          ? "mdi-check"
                          : "mdi-content-copy"
                      }}</v-icon>
                    </v-btn>
                  </template>
                  <span>{{
                    CopiedJudgeId == item.JudgeId ? "Copied!" : "Copy Password"
                  }}</span>
                </v-tooltip>
              </div>
            </template>
          </v-data-table>
        </div>
      </section>

      <section
        v-else-if="!LoadingFlag && !SubmitFlag"
        key="empty"
        class="jdx-empty jdx-reveal"
        style="--d: 200ms"
      >
        <span class="jdx-empty__icon">
          <v-icon size="30" color="#3a67b1">mdi-account-search-outline</v-icon>
        </span>
        <h3 class="jdx-empty__title">No judges to show yet</h3>
        <p class="jdx-empty__desc">
          Use the Judge Finder above to load the judges panel.
        </p>
      </section>
    </transition>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

export default {
  mixins: [common],
  components: {},
  data() {
    return {
      valid1: true,
      search: "",
      ProgessStart: 0,
      uploadPercentage: 0,

      LoadingFlag: false,
      SubmitFlag: false,
      OverlayLoadingFlag: false,
      OverlayLoadingText: "",
      SelectFlag: true,
      DeleteFlag: false,

      alert: {},
      valid: true,
      RoleId: "",
      ResultFlag: 0,

      SearchType: 1,
      MembershipId: "",
      MembershipIdRules: [(v) => !!v || "Membership Id is required"],

      ZoneCodeRules: [(v) => !!v || "Zone is required"],
      JudgeTypeRules: [],
      ZoneCode: "",
      ZoneCodeOptions: [],

      JudgeType: "",
      JudgeTypeOptions: [
        {
          value: "",
          text: "--Select Judge Type--",
        },
        {
          value: 174,
          text: "Zone Head Table",
        },
        {
          value: 175,
          text: "National Head Table",
        },
        {
          value: 196,
          text: "EO",
        },
        {
          value: 173,
          text: "Judge",
        },
        {
          value: 310,
          text: "Nomination Committee Chairman",
        },
        {
          value: 311,
          text: "Nomination Committee Member",
        },
        {
          value: 312,
          text: "OTS Chairman",
        },
        {
          value: 313,
          text: "PA Chairman",
        },
        {
          value: 317,
          text: "National Nomination Committee Chairman",
        },
      ],

      addRecordPrompt: false,
      editRecordPrompt: false,
      previewRecordPrompt: false,

      recordData: {},

      DownloadFlag: false,
      ExcelFileName: "",
      ExcelFields: {},

      StatusUpdatingId: null,
      SwitchKey: 0,
      CopiedJudgeId: null,

      dialog: false,
      dialogDelete: false,
      rows: [],
      tableData1: [],
      tableOptions1: [],
      tableColumns1: [],
      TotalMembers: 0,
      selected1: [],

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      CurrentYearId: 0,
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
      FloatingButtonOptions: {},
    };
  },
  computed: {
    OrderedColumns() {
      // Show the Actions column first
      var columns = this.tableColumns1 || [];
      var actions = columns.filter((c) => c.value == "Actions");
      var others = columns.filter((c) => c.value != "Actions");
      actions = actions.map((c) =>
        Object.assign({}, c, { sortable: false, align: "center", width: 96 })
      );
      return actions.concat(others);
    },
    ActiveCount() {
      return this.tableData1.filter((r) => r.ActiveStatus == 1).length;
    },
    InactiveCount() {
      return this.tableData1.length - this.ActiveCount;
    },
  },
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        var flag = tr.PagePermissionFlag;
        console.log("PagePermissionFlag=" + flag);

        if (!flag) {
          this.$router.push("/access-denied");
        }

        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;
        this.FloatingButtonOptions = tr.FloatingButtonOptions;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        this.CurrentYearId = CurrentJciYearId;

        this.getZoneCodeOptions();
      }
    },
    ZoneCodeOptions: function () {
      console.log("watch ZoneCodeOptions");
      this.ZoneCodeOptionsLoading = false;
    },
    ZoneCode: function () {
      console.log("ZoneCode called");
      this.resetForm();
    },
    SearchType: function () {
      console.log("SearchType called");
      this.resetForm();
      if (this.$refs.form1) {
        this.$refs.form1.resetValidation();
      }
    },
  },
  created() {},
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "award_judges",
        Action: "list",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    resetForm() {
      console.log("resetForm called");
      this.tableData1 = [];
      this.tableOptions1 = [];
      this.tableColumns1 = [];
    },
    copyPassword(item) {
      console.log("copyPassword called");
      var text = item.Password;
      var onCopied = () => {
        this.CopiedJudgeId = item.JudgeId;
        this.toast("success", "Password copied to clipboard");
        setTimeout(() => {
          if (this.CopiedJudgeId == item.JudgeId) {
            this.CopiedJudgeId = null;
          }
        }, 1500);
      };
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard
          .writeText(text)
          .then(onCopied)
          .catch(() => this.toast("error", "Failed to copy password"));
      } else {
        // Fallback for non-https environments
        var textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand("copy");
          onCopied();
        } catch (err) {
          this.toast("error", "Failed to copy password");
        }
        document.body.removeChild(textArea);
      }
    },
    downloadExcel() {
      console.log("downloadExcel called");
      try {
        const excelFields = this.tableOptions1.ExcelFields;
        const exportData = this.tableData1.map((item) => {
          const row = {};
          Object.keys(excelFields).forEach((header) => {
            const fieldName = excelFields[header];
            row[header] = item[fieldName];
          });
          return row;
        });

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Judges");
        XLSX.writeFile(wb, this.ExcelFileName);

        this.toast("success", "Excel file exported successfully");
      } catch (err) {
        console.error("Error downloading file:", err);
        this.toast("error", "Failed to export file");
      }
    },
    getZoneCodeOptions() {
      console.log("getZoneCodeOptions called");
      var selectbox1_source = "ZoneCode";
      var selectbox1_destination = "ZoneCodeOptions";
      var selectbox1_url = "api/zone/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
        NotAvailable: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    submitForm() {
      console.log("submitForm called");

      var result = this.$refs.form1.validate();
      console.log("result=" + result);

      if (result) {
        this.SubmitFlag = true;
        this.OverlayLoadingText = "Loading judges, please wait...";
        this.OverlayLoadingFlag = true;
        this.DownloadFlag = false;
        this.ExcelFileName = "";
        this.ZipDownloadFlag = false;
        this.ZipFileName = "";
        this.tableData1 = [];
        this.selected1 = [];

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/judge/lists";
        var upload = {
          UserInterface: 1,
          SearchType: this.SearchType,
          Zone: this.SearchType == 1 ? this.ZoneCode : "",
          MembershipId: this.SearchType == 2 ? this.MembershipId : "",
          JudgeType: this.JudgeType,
        };
        console.log("upload=" + JSON.stringify(upload));

        const thisIns = this;
        var output = "";
        var records = [];
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));
            thisIns.SubmitFlag = false;
            thisIns.LoadingFlag = false;
            thisIns.OverlayLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            records = response.data.records;

            var options = records.TableOptions;
            thisIns.tableOptions1 = options;

            if (flag == 1) {
              thisIns.tableColumns1 = records.TableHeader;
              thisIns.tableData1 = records.TableData;

              thisIns.DownloadFlag = options.DownloadFlag;
              thisIns.ExcelFileName = options.ExcelFileName;
              thisIns.ExcelFields = options.ExcelFields;

              thisIns.toast("success", output);
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.SubmitFlag = false;
            thisIns.LoadingFlag = false;
            thisIns.OverlayLoadingFlag = false;
          });
      } else {
        var message = "Kindly fill required fields and then submit";
        this.toast("error", message);
      }
    },
    statusToggleAlert(tr, value) {
      console.log("statusToggleAlert called");
      var newStatus = value ? 1 : 0;
      var actionTxt = newStatus == 1 ? "Activate" : "Deactivate";

      var htmlTxt = "";
      htmlTxt += "Judge Name <b> " + tr.MemberFullNameTxt + "</b>";
      htmlTxt += "<br/>Judge Type <b> " + tr.RoleName + "</b>";
      htmlTxt +=
        "<br/><br/>Do you want to " + actionTxt.toLowerCase() + " this judge?";

      Swal.fire({
        title: actionTxt + " Judge?",
        icon: "question",
        html: htmlTxt,
        showCancelButton: true,
        confirmButtonText: actionTxt,
        confirmButtonColor: newStatus == 1 ? "#56BDA3" : "#d33",
        cancelButtonText: `Cancel`,
        cancelButtonColor: "#414042",
      }).then((result) => {
        if (result.isConfirmed) {
          this.updateJudgeStatus(tr, newStatus);
        } else {
          // Re-render the switch so it returns to the saved state
          this.SwitchKey++;
        }
      });
    },
    updateJudgeStatus(tr, newStatus) {
      this.StatusUpdatingId = tr.JudgeId;
      this.OverlayLoadingText =
        newStatus == 1
          ? "Activating judge, please wait..."
          : "Deactivating judge, please wait...";
      this.OverlayLoadingFlag = true;

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/judge/reset-active-status";
      var upload = {
        UserInterface: 1,
        Judge: tr.JudgeId,
        User: tr.UserId,
        Status: newStatus,
      };

      const thisIns = this;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          thisIns.StatusUpdatingId = null;
          thisIns.OverlayLoadingFlag = false;

          var output = response.data.output;
          var flag = response.data.flag;

          if (flag == 1) {
            tr.ActiveStatus = newStatus;
            tr.ActiveStatusTxt = newStatus == 1 ? "Active" : "Inactive";
            tr.ActiveStatusColor = newStatus == 1 ? "green" : "red";
            thisIns.toast("success", output);
          } else {
            thisIns.toast("error", output);
          }
          thisIns.SwitchKey++;
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.StatusUpdatingId = null;
          thisIns.OverlayLoadingFlag = false;
          thisIns.SwitchKey++;
          thisIns.toast("error", "Failed to update judge status");
        });
    },
    deleteAlert(tr) {
      console.log("deleteAlert called");
      var n1 = Object.keys(tr).length;
      if (n1 > 0) {
        this.selectedData = tr;
        this.confirmAlert();
      } else {
        this.sweetAlert("error", "Kindly select one record to delete", false);
      }
    },
    confirmAlert() {
      var tr = this.selectedData;
      var n1 = Object.keys(tr).length;
      if (n1 > 0) {
        var htmlTxt = "";
        htmlTxt +=
          "<br/>Organization Type <b> " + tr.OrganizationTypeName + "</b>";
        htmlTxt += "<br/>Judge Name <b> " + tr.MemberFullNameTxt + "</b>";
        htmlTxt += "<br/>Judge MobileNo <b> " + tr.MobileNo + "</b>";
        htmlTxt += "<br/>Judge EmailId <b> " + tr.EmailId + "</b>";
        htmlTxt += "<br/><br/>Do you want to delete the data?";

        Swal.fire({
          title: "Are you sure?",
          text: "Do you want to delete the data?",
          icon: "warning",
          html: htmlTxt,
          showCancelButton: true,
          confirmButtonText: `Delete`,
          confirmButtonColor: "#d33",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#3A67B1",
        }).then((result) => {
          if (result.isConfirmed) {
            this.deleteRecord(tr);
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      }
    },
    deleteRecord(tr) {
      var n1 = Object.keys(tr).length;
      if (n1 > 0) {
        this.OverlayLoadingText = "Deleting judge, please wait...";
        this.OverlayLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/judge/destroy";
        var upload = {
          UserInterface: 1,
          Judge: tr.JudgeId,
          JudgeName: tr.JudgeName,
          User: tr.UserId,
          Role: tr.RoleId,
          Zone: tr.ZoneId,
        };

        const thisIns = this;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        })
          .then(function (response) {
            thisIns.OverlayLoadingFlag = false;

            var output = response.data.output;
            var flag = response.data.flag;

            if (flag == 1) {
              thisIns.sweetAlert("success", output, false);
              thisIns.submitForm();
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            thisIns.OverlayLoadingFlag = false;
          });
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>

<style lang="scss" scoped>
.jdx {
  /* JCI approved palette */
  --navy: #3a67b1;
  --aqua: #0097d7;
  --gold: #edbe38;
  --seafoam: #56bda3;
  --ink: #0a0f29;
  --text: #414042;
  --white: #ffffff;
  --danger: #c53030;

  --surface: #ffffff;
  --surface-tint: #f5f8fd;
  --line: #e3e9f3;
  --line-strong: #cdd8ea;
  --muted: #6b7280;

  --radius-lg: 22px;
  --radius-md: 16px;
  --radius-sm: 12px;

  --ease: cubic-bezier(0.22, 0.61, 0.36, 1);
  --ease-out: cubic-bezier(0.16, 1, 0.3, 1);
  --t-fast: 180ms;
  --t-med: 280ms;

  --shadow-sm: 0 1px 2px rgba(10, 15, 41, 0.04),
    0 4px 14px rgba(58, 103, 177, 0.07);
  --shadow-md: 0 2px 4px rgba(10, 15, 41, 0.04),
    0 16px 36px rgba(58, 103, 177, 0.13);

  position: relative;
  isolation: isolate;
  padding: 4px 0 32px;
  color: var(--text);
  font-family: "Poppins", "Roboto", sans-serif;
  overflow-x: hidden;
}

/* ---------- Ambient background ---------- */
.jdx-ambient {
  position: absolute;
  inset: 0;
  z-index: -1;
  overflow: hidden;
  pointer-events: none;
}
.jdx-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.35;
  animation: jdx-drift 26s ease-in-out infinite alternate;
}
.jdx-glow--aqua {
  width: 440px;
  height: 440px;
  top: 300px;
  left: -160px;
  background: rgba(0, 151, 215, 0.3);
}
.jdx-glow--navy {
  width: 400px;
  height: 400px;
  top: 720px;
  right: -140px;
  background: rgba(58, 103, 177, 0.25);
  animation-delay: -9s;
}
.jdx-glow--seafoam {
  width: 320px;
  height: 320px;
  bottom: 80px;
  left: 35%;
  background: rgba(86, 189, 163, 0.2);
  animation-delay: -16s;
}

/* ---------- Entrance reveal ---------- */
.jdx-reveal {
  animation: jdx-rise 640ms var(--ease-out) backwards;
  animation-delay: var(--d, 0ms);
}

/* ---------- Hero ---------- */
.jdx-hero {
  position: relative;
  overflow: hidden;
  border-radius: 28px;
  padding: 32px 36px 26px;
  margin-bottom: 22px;
  color: var(--white);
  background: radial-gradient(
      110% 140% at 100% 0%,
      rgba(255, 255, 255, 0.22) 0%,
      rgba(255, 255, 255, 0) 50%
    ),
    radial-gradient(
      80% 120% at 0% 100%,
      rgba(86, 189, 163, 0.45) 0%,
      rgba(86, 189, 163, 0) 60%
    ),
    linear-gradient(125deg, #0082bd 0%, var(--aqua) 50%, #1aaee8 100%);
  box-shadow: 0 24px 60px -24px rgba(0, 151, 215, 0.65);
}
.jdx-hero::before {
  /* slow moving light sweep */
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    115deg,
    transparent 30%,
    rgba(255, 255, 255, 0.1) 45%,
    transparent 60%
  );
  background-size: 250% 100%;
  animation: jdx-sweep 9s linear infinite;
  pointer-events: none;
}
.jdx-hero::after {
  /* fine grid texture */
  content: "";
  position: absolute;
  inset: 0;
  background-image: linear-gradient(
      rgba(255, 255, 255, 0.07) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(255, 255, 255, 0.07) 1px, transparent 1px);
  background-size: 34px 34px;
  mask-image: linear-gradient(90deg, transparent 0%, #000 75%);
  -webkit-mask-image: linear-gradient(90deg, transparent 0%, #000 75%);
  pointer-events: none;
}
.jdx-hero__decor {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
}
.jdx-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(40px);
  animation: jdx-drift 18s ease-in-out infinite alternate;
}
.jdx-orb--1 {
  width: 240px;
  height: 240px;
  right: 24%;
  bottom: -150px;
  background: rgba(86, 189, 163, 0.45);
}
.jdx-orb--2 {
  width: 170px;
  height: 170px;
  left: 42%;
  top: -90px;
  background: rgba(237, 190, 56, 0.26);
  animation-delay: -7s;
}
.jdx-ring {
  position: absolute;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.2);
}
.jdx-ring--1 {
  width: 320px;
  height: 320px;
  right: -90px;
  top: -150px;
  border-style: dashed;
  animation: jdx-spin 60s linear infinite;
}
.jdx-ring--2 {
  width: 200px;
  height: 200px;
  right: -30px;
  top: -90px;
}
.jdx-spark {
  position: absolute;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--white);
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.9),
    0 0 18px rgba(255, 255, 255, 0.6);
  animation: jdx-twinkle 3.2s ease-in-out infinite;
}
.jdx-spark--1 {
  left: 56%;
  top: 18%;
}
.jdx-spark--2 {
  left: 72%;
  top: 64%;
  animation-delay: -1.1s;
}
.jdx-spark--3 {
  left: 8%;
  top: 88%;
  width: 4px;
  height: 4px;
  animation-delay: -2s;
}
.jdx-spark--4 {
  left: 34%;
  top: 12%;
  width: 4px;
  height: 4px;
  background: var(--gold);
  box-shadow: 0 0 12px rgba(237, 190, 56, 0.9);
  animation-delay: -0.6s;
}
.jdx-hero__cards {
  position: absolute;
  right: 36px;
  bottom: -18px;
  width: 220px;
  height: 140px;

  rect,
  circle {
    fill: rgba(255, 255, 255, 0.12);
    stroke: rgba(255, 255, 255, 0.32);
    stroke-width: 1;
  }
  line {
    stroke: rgba(255, 255, 255, 0.38);
    stroke-width: 2;
    stroke-linecap: round;
  }
  .jdx-card {
    transform-box: fill-box;
    transform-origin: bottom center;
    animation: jdx-deal 900ms var(--ease-out) backwards;
  }
  .jdx-card--1 {
    transform: rotate(-8deg);
    animation-delay: 120ms;
  }
  .jdx-card--2 {
    animation-delay: 220ms;

    rect {
      fill: rgba(237, 190, 56, 0.3);
      stroke: rgba(237, 190, 56, 0.8);
    }
    circle {
      fill: rgba(237, 190, 56, 0.85);
      stroke: none;
    }
  }
  .jdx-card--3 {
    transform: rotate(8deg);
    animation-delay: 320ms;
  }
}

.jdx-hero__top {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 18px 24px;
}
.jdx-hero__grid {
  display: flex;
  align-items: center;
  gap: 22px;
  min-width: 0;
  flex: 1 1 420px;
}
.jdx-hero__icon {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 68px;
  height: 68px;
  border-radius: 20px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.3),
    rgba(255, 255, 255, 0.08)
  );
  border: 1px solid rgba(255, 255, 255, 0.36);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.3),
    0 14px 30px -12px rgba(10, 15, 41, 0.45);
  backdrop-filter: blur(6px);
  animation: jdx-pop 700ms var(--ease-out) 200ms backwards;
}
.jdx-hero__copy {
  min-width: 0;
}
.jdx-hero__eyebrow {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 0.74rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 8px;

  em {
    font-family: "Lora", Georgia, serif;
    font-style: italic;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: none;
    font-size: 1.02rem;
    color: var(--gold);
    text-shadow: 0 1px 8px rgba(10, 15, 41, 0.25);
  }
}
.jdx-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--gold);
}
.jdx-hero__title {
  margin: 0;
  font-size: clamp(1.5rem, 1.05rem + 1.6vw, 2.3rem);
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.01em;
  color: var(--white);
  text-shadow: 0 2px 14px rgba(10, 15, 41, 0.18);
  overflow-wrap: anywhere;
}
.jdx-hero__accent {
  display: block;
  width: 92px;
  height: 3px;
  margin: 12px 0 10px;
  border-radius: 3px;
  background: linear-gradient(
    90deg,
    var(--gold),
    var(--white),
    var(--seafoam),
    var(--gold)
  );
  background-size: 300% 100%;
  transform-origin: left center;
  animation: jdx-gradient 6s linear infinite,
    jdx-grow 900ms var(--ease-out) 300ms both;
}
.jdx-hero__desc {
  margin: 0;
  max-width: 600px;
  font-size: 0.95rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.92);
}

/* Hero buttons */
.jdx-hero__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}
.jdx-glass-btn.v-btn,
.jdx-gold-btn.v-btn {
  height: 42px;
  padding: 0 18px;
  border-radius: 13px;
  font-size: 0.86rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: none;
  transition: background-color var(--t-fast) var(--ease),
    transform var(--t-med) var(--ease), box-shadow var(--t-med) var(--ease);

  &:hover {
    transform: translateY(-2px);
  }
  &:active {
    transform: translateY(0);
  }
  &:focus-visible {
    outline: 3px solid rgba(255, 255, 255, 0.75);
    outline-offset: 2px;
  }
}
.jdx-glass-btn.v-btn {
  color: var(--white) !important;
  background: rgba(255, 255, 255, 0.14) !important;
  border: 1px solid rgba(255, 255, 255, 0.45);
  backdrop-filter: blur(6px);

  .jdx-glass-btn__icon {
    color: var(--white) !important;
    transition: transform 500ms var(--ease);
  }
  &:hover {
    background: rgba(255, 255, 255, 0.24) !important;
    box-shadow: 0 12px 24px -14px rgba(10, 15, 41, 0.6);
  }
  &:hover .jdx-spin-hover {
    transform: rotate(180deg);
  }
}
.jdx-gold-btn.v-btn {
  color: var(--ink) !important;
  background: var(--gold) !important;
  box-shadow: 0 12px 26px -12px rgba(10, 15, 41, 0.55);

  .jdx-gold-btn__icon {
    color: var(--ink) !important;
    transition: transform var(--t-med) var(--ease);
  }
  &:hover {
    box-shadow: 0 18px 32px -12px rgba(10, 15, 41, 0.6);
    filter: brightness(1.04);
  }
  &:hover .jdx-gold-btn__icon {
    transform: scale(1.12);
  }
}

/* Hero stats */
.jdx-stats {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  max-width: 760px;
  margin-top: 26px;
}
.jdx-stat {
  --accent: var(--white);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
  padding: 14px 16px;
  border-radius: var(--radius-md);
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.28);
  backdrop-filter: blur(8px);
  transition: transform var(--t-med) var(--ease),
    background-color var(--t-med) var(--ease);

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 14px;
    bottom: 14px;
    width: 4px;
    border-radius: 0 4px 4px 0;
    background: var(--accent);
  }
  &:hover {
    transform: translateY(-2px);
    background: rgba(255, 255, 255, 0.2);
  }
  &:hover .jdx-stat__icon {
    transform: rotate(-6deg) scale(1.08);
  }
}
.jdx-stat--active {
  --accent: var(--seafoam);
}
.jdx-stat--inactive {
  --accent: #ffb4b4;
}
.jdx-stat__icon {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: transform var(--t-med) var(--ease);
}
.jdx-stat--active .jdx-stat__icon {
  background: var(--seafoam);
  border-color: rgba(255, 255, 255, 0.45);
}
.jdx-stat__body {
  min-width: 0;
  flex: 1 1 auto;
}
.jdx-stat__value {
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1.1;
  color: var(--white);
  font-variant-numeric: tabular-nums;
}
.jdx-stat__label {
  margin-top: 2px;
  font-size: 0.78rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.jdx-meter {
  margin-top: 8px;
  height: 4px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.25);
  overflow: hidden;
}
.jdx-meter__fill {
  display: block;
  height: 100%;
  border-radius: 4px;
  background: var(--seafoam);
  transition: width 700ms var(--ease-out);
}

/* ---------- Shared section head ---------- */
.jdx-head {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}
.jdx-head__icon {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 14px;
  transition: transform var(--t-med) var(--ease);
}
.jdx-head__icon--navy {
  background: linear-gradient(135deg, var(--navy), var(--aqua));
  box-shadow: 0 10px 22px -10px rgba(58, 103, 177, 0.8);
}
.jdx-head__title {
  margin: 0;
  font-size: 1.12rem;
  font-weight: 700;
  color: var(--ink);
  line-height: 1.3;
}
.jdx-head__desc {
  margin: 2px 0 0;
  font-size: 0.84rem;
  color: var(--muted);
}

/* ---------- Judge finder ---------- */
.jdx-studio {
  position: relative;
  padding: 24px 28px 22px;
  margin-bottom: 26px;
  border-radius: var(--radius-lg);
  background: linear-gradient(180deg, #ffffff, var(--surface-tint));
  border: 1px solid var(--line);
  box-shadow: var(--shadow-sm);
  transition: box-shadow var(--t-med) var(--ease);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 28px;
    right: 28px;
    height: 3px;
    border-radius: 0 0 3px 3px;
    background: linear-gradient(
      90deg,
      var(--navy),
      var(--aqua),
      var(--seafoam),
      var(--gold)
    );
  }
  &:hover {
    box-shadow: var(--shadow-md);
  }
  &:hover .jdx-head__icon {
    transform: rotate(-6deg) scale(1.05);
  }
}
.jdx-studio__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 14px 20px;
  margin-bottom: 20px;
}

/* Segmented control */
.jdx-segmented.v-btn-toggle {
  padding: 4px;
  border-radius: 14px;
  background: var(--white) !important;
  border: 1px solid var(--line);
  box-shadow: inset 0 1px 2px rgba(10, 15, 41, 0.04);

  > .v-btn.v-btn {
    height: 36px;
    padding: 0 14px;
    border: none !important;
    border-radius: 10px !important;
    font-size: 0.84rem;
    font-weight: 600;
    letter-spacing: 0;
    text-transform: none;
    color: var(--muted) !important;
    background: transparent !important;
    transition: background-color var(--t-fast) var(--ease),
      color var(--t-fast) var(--ease);

    .v-icon {
      color: inherit !important;
    }
    &::before {
      opacity: 0 !important;
    }
    &:hover {
      color: var(--navy) !important;
    }
  }
  > .v-btn.v-btn--active {
    color: var(--white) !important;
    background: var(--navy) !important;
    box-shadow: 0 8px 18px -10px rgba(58, 103, 177, 0.9);
  }
}

/* Loading */
.jdx-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 26px 0 18px;
}
.jdx-loading__orb {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.jdx-loading__halo {
  position: absolute;
  width: 110px;
  height: 110px;
  border-radius: 50%;
  background: radial-gradient(rgba(0, 151, 215, 0.25), transparent 70%);
  animation: jdx-breathe 2.4s ease-in-out infinite;
}
.jdx-loading__icon {
  position: absolute !important;
}
.jdx-loading__text {
  margin: 14px 0 0;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--muted);
}

/* Fields */
.jdx-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px 22px;
  max-width: 820px;
}
.jdx-field__label {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--ink);
}
.jdx-field__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 8px;
}
.jdx-field__icon--navy {
  background: var(--navy);
}
.jdx-field__icon--aqua {
  background: var(--aqua);
}
.jdx-req {
  color: var(--danger);
}
.jdx-field,
.jdx-toolbar {
  ::v-deep .v-text-field--outlined > .v-input__control > .v-input__slot {
    background: var(--white);
    border-radius: var(--radius-sm);
    transition: box-shadow var(--t-med) var(--ease);
  }
  ::v-deep .v-text-field--outlined fieldset {
    border-color: var(--line-strong);
    border-radius: var(--radius-sm);
    transition: border-color var(--t-fast) var(--ease);
  }
  ::v-deep
    .v-text-field--outlined:not(.v-input--is-focused):not(.error--text)
    > .v-input__control
    > .v-input__slot:hover
    fieldset {
    border-color: var(--navy);
  }
  ::v-deep .v-input--is-focused > .v-input__control > .v-input__slot {
    box-shadow: 0 0 0 4px rgba(0, 151, 215, 0.14);
  }
  ::v-deep .v-input--is-focused:not(.error--text) fieldset {
    border-color: var(--aqua);
  }
  ::v-deep .v-input__prepend-inner .v-icon {
    color: var(--navy);
  }
}

/* Finder actions */
.jdx-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 6px;
  padding-top: 18px;
  border-top: 1px dashed var(--line-strong);
}
.jdx-required-note {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--muted);
  padding: 6px 12px;
  border-radius: 999px;
  background: var(--white);
  border: 1px solid var(--line);
}
.jdx-btn.v-btn {
  height: 46px;
  padding: 0 22px;
  border-radius: 14px;
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-transform: none;
  transition: transform var(--t-med) var(--ease),
    box-shadow var(--t-med) var(--ease), filter var(--t-fast) var(--ease);

  .jdx-btn__icon {
    transition: transform var(--t-med) var(--ease);
  }
  &:not(.v-btn--disabled):hover {
    transform: translateY(-2px);
    filter: brightness(1.05);
  }
  &:not(.v-btn--disabled):hover .jdx-btn__icon {
    transform: translateX(-1px) scale(1.12);
  }
  &:not(.v-btn--disabled):active {
    transform: translateY(0);
  }
  &:focus-visible {
    outline: 3px solid rgba(0, 151, 215, 0.55);
    outline-offset: 2px;
  }
}
.jdx-btn--search.v-btn:not(.v-btn--disabled) {
  min-width: 190px;
  background: var(--navy) !important;
  color: var(--white) !important;
  box-shadow: 0 12px 26px -12px rgba(58, 103, 177, 0.9);

  .v-icon {
    color: var(--white) !important;
  }
  &:hover {
    box-shadow: 0 18px 32px -12px rgba(58, 103, 177, 0.9);
  }
}
.jdx-btn--export.v-btn {
  height: 42px;
  background: var(--seafoam) !important;
  color: var(--ink) !important;
  box-shadow: 0 12px 26px -14px rgba(86, 189, 163, 0.95);

  .v-icon {
    color: var(--ink) !important;
  }
}

/* ---------- Results group ---------- */
.jdx-group {
  position: relative;
  margin-bottom: 30px;
}
.jdx-group__head {
  position: relative;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  padding: 0 0 14px;
  margin-bottom: 16px;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 2px;
    border-radius: 2px;
    background: linear-gradient(
      90deg,
      var(--aqua) 0%,
      rgba(0, 151, 215, 0.12) 35%,
      transparent 100%
    );
  }
}
.jdx-group__icon {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: var(--aqua);
  box-shadow: 0 10px 20px -12px var(--aqua);
}
.jdx-group__copy {
  flex: 1 1 260px;
  min-width: 0;
}
.jdx-group__title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--ink);
}
.jdx-group__desc {
  margin: 2px 0 0;
  font-size: 0.82rem;
  color: var(--muted);
}
.jdx-count {
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--navy);
  background: rgba(58, 103, 177, 0.1);
  font-variant-numeric: tabular-nums;
}
.jdx-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.jdx-legend__item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text);
  background: var(--white);
  border: 1px solid var(--line);
}
.jdx-legend__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
.jdx-legend__dot--active {
  background: var(--seafoam);
  box-shadow: 0 0 0 3px rgba(86, 189, 163, 0.2);
}
.jdx-legend__dot--inactive {
  background: var(--danger);
  box-shadow: 0 0 0 3px rgba(197, 48, 48, 0.15);
}

/* Table card */
.jdx-table-card {
  position: relative;
  overflow: hidden;
  border-radius: var(--radius-lg);
  background: var(--surface);
  border: 1px solid var(--line);
  box-shadow: var(--shadow-sm);
  transition: box-shadow var(--t-med) var(--ease),
    border-color var(--t-med) var(--ease);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 22px;
    width: 56px;
    height: 3px;
    border-radius: 0 0 3px 3px;
    background: var(--aqua);
    z-index: 1;
    transition: width var(--t-med) var(--ease);
  }
  &:hover {
    border-color: var(--line-strong);
    box-shadow: var(--shadow-md);
  }
  &:hover::before {
    width: 110px;
  }
}
.jdx-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid var(--line);
  background: linear-gradient(180deg, #ffffff, var(--surface-tint));
}
.jdx-toolbar__search {
  flex: 0 1 380px;
  min-width: 0;
}

.jdx-table {
  ::v-deep thead th {
    height: 48px !important;
    background: var(--surface-tint) !important;
    color: var(--muted) !important;
    font-size: 0.7rem !important;
    font-weight: 700 !important;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    white-space: nowrap;
    border-bottom: 1px solid var(--line) !important;
  }
  ::v-deep tbody tr {
    transition: background-color var(--t-fast) var(--ease);

    &:hover {
      background: rgba(0, 151, 215, 0.04) !important;
    }
    td {
      height: 66px !important;
      font-size: 0.84rem !important;
      color: var(--text);
      border-bottom: 1px solid #eef2f8 !important;
    }
  }
  ::v-deep .v-data-footer {
    padding: 4px 16px;
    border-top: 1px solid var(--line);
    font-size: 0.8rem;
    color: var(--muted);
  }
}

.jdx-row-actions {
  display: flex;
  justify-content: center;
  gap: 6px;
}
.jdx-row-action.v-btn {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid var(--line);
  background: var(--white);
  transition: transform var(--t-fast) var(--ease),
    background-color var(--t-fast) var(--ease),
    border-color var(--t-fast) var(--ease);

  &:hover {
    transform: translateY(-1px);
  }
}
.jdx-row-action--view.v-btn {
  color: var(--aqua) !important;

  &:hover {
    background: rgba(0, 151, 215, 0.08);
    border-color: rgba(0, 151, 215, 0.4);
  }
}
.jdx-row-action--delete.v-btn {
  color: var(--danger) !important;

  &:hover {
    background: rgba(197, 48, 48, 0.07);
    border-color: rgba(197, 48, 48, 0.35);
  }
}

.jdx-status {
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}
.jdx-switch {
  ::v-deep .v-input--selection-controls__input {
    margin-right: 0;
  }
}
.jdx-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--text);
  transition: background-color var(--t-med) var(--ease);

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    margin-right: 6px;
    border-radius: 50%;
  }
  &.is-active {
    background: rgba(86, 189, 163, 0.16);

    &::before {
      background: var(--seafoam);
    }
  }
  &.is-inactive {
    background: rgba(197, 48, 48, 0.09);
    color: var(--danger);

    &::before {
      background: var(--danger);
    }
  }
}
.jdx-name {
  font-weight: 600;
  color: var(--ink);
}
.jdx-tag {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 0.76rem;
  font-weight: 600;
  white-space: nowrap;
  color: var(--ink);
  background: rgba(0, 151, 215, 0.09);
  border-left: 3px solid var(--aqua);
}
.jdx-mono {
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 0.92rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--ink) !important;
  background: var(--surface-tint) !important;
  border: 1px solid var(--line);
  box-shadow: none !important;
}
.jdx-secret {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}
.jdx-row-action--copy.v-btn {
  width: 30px;
  height: 30px;
  color: var(--navy) !important;

  &:hover {
    background: rgba(58, 103, 177, 0.08);
    border-color: rgba(58, 103, 177, 0.4);
  }
}
.jdx-avatar {
  border: 2px solid var(--white);
  box-shadow: 0 0 0 1px var(--line-strong), 0 4px 10px rgba(10, 15, 41, 0.12);
  transition: transform var(--t-med) var(--ease);

  &:hover {
    transform: scale(1.06);
  }
}
.jdx-contact {
  line-height: 1.6;
  color: var(--text);
}

/* ---------- Empty state ---------- */
.jdx-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 20px;
  border-radius: var(--radius-lg);
  border: 1px dashed var(--line-strong);
  background: rgba(255, 255, 255, 0.7);
}
.jdx-empty__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin-bottom: 14px;
  border-radius: 20px;
  background: rgba(58, 103, 177, 0.1);
}
.jdx-empty__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: var(--ink);
}
.jdx-empty__desc {
  margin: 4px 0 0;
  font-size: 0.85rem;
  color: var(--muted);
}

/* ---------- Transitions ---------- */
.jdx-fade-enter-active,
.jdx-fade-leave-active {
  transition: opacity 350ms cubic-bezier(0.2, 0.8, 0.2, 1),
    transform 350ms cubic-bezier(0.2, 0.8, 0.2, 1);
}
.jdx-fade-enter,
.jdx-fade-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

/* ---------- Responsive ---------- */
@media (max-width: 1263px) {
  .jdx-hero__cards {
    width: 180px;
    right: 20px;
    opacity: 0.8;
  }
}
@media (max-width: 959px) {
  .jdx-hero {
    padding: 28px 26px 24px;
  }
  .jdx-hero__cards {
    opacity: 0.45;
  }
  .jdx-stats {
    max-width: none;
  }
}
@media (max-width: 599px) {
  .jdx {
    padding-bottom: 20px;
  }
  .jdx-hero {
    padding: 22px 18px 20px;
    border-radius: 22px;
    margin-bottom: 16px;
  }
  .jdx-hero__cards,
  .jdx-ring {
    display: none;
  }
  .jdx-hero__grid {
    align-items: flex-start;
    gap: 14px;
    flex-basis: 100%;
  }
  .jdx-hero__icon {
    width: 52px;
    height: 52px;
    border-radius: 16px;
  }
  .jdx-hero__actions {
    width: 100%;
  }
  .jdx-glass-btn.v-btn {
    width: 42px;
    min-width: 0 !important;
    padding: 0;

    .jdx-glass-btn__icon {
      margin: 0 !important;
    }
  }
  .jdx-glass-btn__text {
    display: none;
  }
  .jdx-gold-btn.v-btn {
    flex: 1 1 auto;
  }
  .jdx-stats {
    gap: 8px;
    margin-top: 20px;
  }
  .jdx-stat {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 12px;
  }
  .jdx-stat__icon {
    width: 32px;
    height: 32px;
    border-radius: 10px;
  }
  .jdx-stat__value {
    font-size: 1.3rem;
  }
  .jdx-studio {
    padding: 18px 16px;
    margin-bottom: 18px;
  }
  .jdx-segmented.v-btn-toggle {
    width: 100%;

    > .v-btn.v-btn {
      flex: 1 1 0;
      padding: 0 8px;
    }
  }
  .jdx-fields {
    grid-template-columns: minmax(0, 1fr);
  }
  .jdx-actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }
  .jdx-required-note {
    align-self: flex-start;
  }
  .jdx-btn.v-btn {
    width: 100%;
  }
  .jdx-toolbar {
    padding: 14px;
  }
  .jdx-toolbar__search {
    flex-basis: 100%;
  }
  .jdx-legend {
    width: 100%;
  }
}

/* ---------- Reduced motion ---------- */
@media (prefers-reduced-motion: reduce) {
  .jdx *,
  .jdx *::before,
  .jdx *::after {
    animation-duration: 1ms !important;
    animation-iteration-count: 1 !important;
    animation-delay: 0ms !important;
    transition-duration: 1ms !important;
  }
  .jdx-spark,
  .jdx-hero::before {
    display: none;
  }
}

/* ---------- Keyframes ---------- */
@keyframes jdx-rise {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
@keyframes jdx-pop {
  from {
    opacity: 0;
    transform: scale(0.6) rotate(-12deg);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
@keyframes jdx-grow {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}
@keyframes jdx-deal {
  from {
    opacity: 0;
    transform: translateY(40px) rotate(0deg);
  }
}
@keyframes jdx-gradient {
  from {
    background-position: 0% 50%;
  }
  to {
    background-position: 300% 50%;
  }
}
@keyframes jdx-sweep {
  from {
    background-position: 150% 0;
  }
  to {
    background-position: -100% 0;
  }
}
@keyframes jdx-drift {
  0% {
    transform: translate(0, 0) scale(1);
  }
  50% {
    transform: translate(30px, -20px) scale(1.06);
  }
  100% {
    transform: translate(-20px, 25px) scale(0.96);
  }
}
@keyframes jdx-twinkle {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.35;
    transform: scale(0.7);
  }
}
@keyframes jdx-spin {
  to {
    transform: rotate(360deg);
  }
}
@keyframes jdx-breathe {
  0%,
  100% {
    opacity: 0.6;
    transform: scale(0.9);
  }
  50% {
    opacity: 1;
    transform: scale(1.08);
  }
}
</style>
